import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { BotonIcono } from '@/ui/Boton'

/**
 * Los atajos escritos, abiertos con `?`.
 *
 * "Un producto de trabajo que no se puede operar con teclado no es un producto
 * de trabajo." Y si los atajos no están escritos en ningún sitio, no existen.
 */

const ATAJOS: readonly { combinacion: string; accion: string }[] = [
  { combinacion: 'Ctrl+K', accion: 'Cambiar de cliente' },
  { combinacion: 'Ctrl+/', accion: 'Búsqueda global' },
  { combinacion: 'N', accion: 'Nueva factura' },
  { combinacion: 'Ctrl+Enter', accion: 'Emitir el documento abierto' },
  { combinacion: 'Esc', accion: 'Cerrar panel o modal' },
  { combinacion: '?', accion: 'Ver todos los atajos' },
]

export function PanelAtajos({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const dialogo = useRef<HTMLDivElement>(null)
  const focoPrevio = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!abierto) return
    focoPrevio.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    dialogo.current?.querySelector('button')?.focus()

    const alTeclear = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') onCerrar()
    }
    document.addEventListener('keydown', alTeclear, true)
    return () => {
      document.removeEventListener('keydown', alTeclear, true)
      focoPrevio.current?.focus()
    }
  }, [abierto, onCerrar])

  if (!abierto) return null

  return (
    <div className="fixed inset-0 z-[var(--capa-modal)] flex items-center justify-center p-4 sin-impresion">
      <div aria-hidden="true" onClick={onCerrar} className="absolute inset-0 bg-gris-900/30" />
      <div
        ref={dialogo}
        role="dialog"
        aria-modal="true"
        aria-label="Atajos de teclado"
        className="relative w-full max-w-[420px] rounded-md border border-gris-300 bg-white shadow-flotante"
      >
        <header className="flex items-center justify-between border-b border-gris-300 px-4 py-3">
          <h2 className="text-seccion text-gris-900">Atajos de teclado</h2>
          <BotonIcono etiqueta="Cerrar" icono={<X />} onClick={onCerrar} />
        </header>
        <dl className="p-4">
          {ATAJOS.map((atajo) => (
            <div key={atajo.combinacion} className="flex items-center justify-between gap-4 py-1">
              <dt className="text-cuerpo text-gris-900">{atajo.accion}</dt>
              <dd className="mono rounded-md border border-gris-300 bg-gris-50 px-2 py-px text-gris-700">
                {atajo.combinacion}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}
